import { useState, useEffect, useRef } from "react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import DateTimePopup from "./DateTimePopup";

/**
 * DateTimePicker component for selecting date and time.
 * @param {Object} props
 * @param {string} props.value - The current value of the picker (ISO string or null).
 * @param {function} props.onChange - Callback when the date/time changes.
 * @param {boolean} [props.disabled] - If true, disables the picker.
 * @param {string} [props.inputClass] - Additional class for the input (not used).
 * @param {boolean} [props.clearableDatePicker] - If true, shows a clear button.
 * @param {boolean} [props.matchCustomInputStyle] - If true, uses border/padding consistent with CustomInput (gray-100 border, same as dropdowns).
 */
const DateTimePicker = ({ value, onChange, disabled, clearableDatePicker, label, matchCustomInputStyle = false }) => {
  /**
   * Parses a value into a Date object or null.
   * @param {string|Date|null} val
   * @returns {Date|null}
   */
  const parseValue = (val) => {
    if (!val) return null;
    const date = new Date(val);
    return isNaN(date.getTime()) ? null : date;
  };

  const [selectedDate, setSelectedDate] = useState(parseValue(value));
  const [selectedTime, setSelectedTime] = useState(
    parseValue(value) || new Date()
  );
  const [expanded, setExpanded] = useState(false);
  const wrapperRef = useRef();

  useEffect(() => {
    const parsed = parseValue(value);
    if (parsed) {
      setSelectedDate(parsed);
      setSelectedTime(parsed);
    } else {
      // Parent cleared value (e.g. Cancel on follow-up) — must reset internal state or old date stays visible
      setSelectedDate(null);
      setSelectedTime(new Date());
    }
  }, [value]);

  useEffect(() => {
    /**
     * Handles clicks outside the picker (input + popup) to close it.
     * @param {MouseEvent} e
     */
    const handleOutsideClick = (e) => {
      if (!wrapperRef.current) return;
      const clickedInsideInput = wrapperRef.current.contains(e.target);
      const clickedInsidePopup =
        typeof e.target?.closest === "function" &&
        !!e.target.closest(".hc-datetime-popup");

      if (!clickedInsideInput && !clickedInsidePopup) {
        setExpanded(false);
      }
    };

    if (expanded) {
      document.addEventListener("mousedown", handleOutsideClick);
      return () => document.removeEventListener("mousedown", handleOutsideClick);
    }
  }, [expanded]);

  /**
   * Formats a Date object for display.
   * @param {Date|null} date
   * @returns {{date: string, time: string}}
   */
  const formatDisplayValue = (date) => {
    if (!date) return { date: "YYYY-MM-DD", time: "00:00 AM" };
    
    const dateStr = date.toLocaleDateString('en-CA');
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
    return { date: dateStr, time: timeStr };
  };

  const displayValue = formatDisplayValue(
    selectedDate && selectedTime
      ? new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate(),
          selectedTime.getHours(),
          selectedTime.getMinutes()
        )
      : null
  );

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleTimeChange = (newTime) => {
    setSelectedTime(newTime);
  };

  /**
   * Handles booking/confirming the selected date and time.
   */
  const handleBook = () => {
    if (!selectedDate || !selectedTime) return;
    const finalDateTime = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      selectedTime.getHours(),
      selectedTime.getMinutes()
    );
    // Manually format the date and time to avoid toISOString's UTC conversion
    const year = finalDateTime.getFullYear();
    const month = (finalDateTime.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
    const day = finalDateTime.getDate().toString().padStart(2, '0');
    const hours = finalDateTime.getHours().toString().padStart(2, '0');
    const minutes = finalDateTime.getMinutes().toString().padStart(2, '0');

    const formattedDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
    onChange(formattedDateTime); // Pass the locally formatted string
    setExpanded(false);
  };

  /**
   * Clears the selected date and time.
   * @param {Event} e
   */
  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedDate(null);
    setSelectedTime(new Date());
    onChange(null);
  };

  /**
   * Calendar icon SVG component.
   * @param {Object} props
   * @param {string} [props.className]
   * @returns {JSX.Element}
   */
  const CalendarIcon = ({ className }) => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M2 2.68182H22C22.8284 2.68182 23.5 3.35339 23.5 4.18182V9.31818H0.5V4.18182C0.5 3.35339 1.17157 2.68182 2 2.68182Z"
        fill="#0B4C5B"
        stroke="#0B4C5B"
      />
      <rect
        x="2.68176"
        y="0.5"
        width="1.18182"
        height="3.36364"
        rx="0.590909"
        fill="#0B4C5B"
        stroke="#0B4C5B"
      />
      <rect
        x="20.1364"
        y="0.5"
        width="1.18182"
        height="3.36364"
        rx="0.590909"
        fill="#0B4C5B"
        stroke="#0B4C5B"
      />
      <mask id="path-4-inside-1_115_240" fill="white">
        <path d="M0 10.9091H24V22C24 23.1046 23.1046 24 22 24H2C0.89543 24 0 23.1046 0 22V10.9091Z" />
      </mask>
      <path
        d="M0 10.9091H24H0ZM25 22C25 23.6569 23.6569 25 22 25H2C0.343146 25 -1 23.6569 -1 22H1C1 22.5523 1.44772 23 2 23H22C22.5523 23 23 22.5523 23 22H25ZM2 25C0.343146 25 -1 23.6569 -1 22V10.9091H1V22C1 22.5523 1.44772 23 2 23V25ZM25 10.9091V22C25 23.6569 23.6569 25 22 25V23C22.5523 23 23 22.5523 23 22V10.9091H25Z"
        fill="#0B4C5B"
        mask="url(#path-4-inside-1_115_240)"
      />
      <rect
        x="3.27271"
        y="12"
        width="4.36364"
        height="3.27273"
        rx="1"
        fill="#0B4C5B"
      />
      <rect
        x="3.27271"
        y="17.4545"
        width="4.36364"
        height="3.27273"
        rx="1"
        fill="#0B4C5B"
      />
      <rect
        x="16.3636"
        y="12"
        width="4.36364"
        height="3.27273"
        rx="1"
        fill="#0B4C5B"
      />
      <rect
        x="16.3636"
        y="17.4545"
        width="4.36364"
        height="3.27273"
        rx="1"
        fill="#0B4C5B"
      />
      <rect
        x="9.81824"
        y="12"
        width="4.36364"
        height="3.27273"
        rx="1"
        fill="#0B4C5B"
      />
      <rect
        x="9.81824"
        y="17.4545"
        width="4.36364"
        height="3.27273"
        rx="1"
        fill="#0B4C5B"
      />
    </svg>
  );

  const isNested = matchCustomInputStyle;
  const triggerClasses = isNested
    ? "w-full border-0 rounded-none p-0 bg-transparent cursor-pointer flex flex-col justify-center gap-0.5"
    : "w-full border border-gray-900 rounded-md py-[22px] px-5 cursor-pointer bg-white transition-colors";
  const labelClasses = isNested
    ? "block mb-0.5 font-normal text-gray-900 text-xs leading-[18px]"
    : "block text-xs font-medium text-gray-800 mb-1 leading-tight";

  return (
    <div ref={wrapperRef} className="relative w-full overflow-visible">
      <div
        className={`${triggerClasses} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        onClick={() => !disabled && setExpanded(true)}
      >
        {label && <label className={labelClasses}>{label}</label>}
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-2 text-gray-700 text-sm">
            <span>{displayValue.date}</span>
            <span className="text-gray-400">|</span>
            <span>{displayValue.time}</span>
          </div>
          <div className="flex items-center space-x-1">
            {clearableDatePicker && !!value && (
              <>
                <XMarkIcon
                  className="h-4 w-4 text-teal-1200 hover:text-gray-600 cursor-pointer transition-colors"
                  onClick={handleClear}
                />
                <span className="text-gray-300">|</span>
              </>
            )}
            <CalendarIcon className="h-5 w-5" />
          </div>
        </div>
      </div>
      {/* Expanded date/time picker */}
      <DateTimePopup
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        onDateSelect={handleDateSelect}
        onTimeChange={handleTimeChange}
        onBook={handleBook}
        wrapperRef={wrapperRef}
        open={expanded}
      />
    </div>
  );
};

export default DateTimePicker;